import {
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Spin,
  Table,
  message,
} from "antd";
import WeightageInput from "./WeightageInput";
import KpiViewModal from "./KpiViewModal";
import UserFloatingButton from "pages/Performance/UserFloatingButton";
import { RightOutlined, DeleteOutlined, PlusOutlined, CopyOutlined } from "@ant-design/icons";
import COLORS from "common/Colors";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";
import TextLink from "./TextLink";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import { getUserInfo, getUserList } from "redux/selectors";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";

const { TextArea } = Input;

const Wrap = styled.div`
  .breadcrumb {
    display: flex;
    align-items: center;
  }
  .perspective-title {
    font-weight: 600;
    margin: 16px 0 8px;
  }
  .footer {
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    background: #fff;
    padding: 10px;
    display: flex;
    justify-content: end;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    margin: 20px -20px 0;
  }
  .review-checkbox-row {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }

    .review-checkbox-label {
      width: 140px;
      flex: 0 0 140px;
    }

    .ant-picker {
      width: 180px;
    }
  }
  .ant-form-item {
    margin-bottom: 0;
  }
  .ant-table-thead > tr > th {
    background: #C0CDE2 !important;
    font-weight: 600;
    font-size: 13px;
    color: #000000;
  }
  .ant-table-sticky-holder {
    background: #fff;
    z-index: 3;
  }
  .ant-table-tbody > tr > td {
    font-size: 13px;
    padding: 8px 12px;
  }
  .planning-group-row > td {
    background: #fafafa;
  }
  .planning-spacer-row {
    pointer-events: none;
  }
  .planning-spacer-row > td {
    height: 10px;
    padding: 0 !important;
    border: none !important;
    background: #fff;
  }
  .decline-reason-box {
    background: #f5f5f5;
    border-radius: 6px;
    padding: 16px 20px;
    margin-top: 40px;

    .decline-reason-label {
      color: #8c8c8c;
      margin-bottom: 6px;
    }
  }
  /* WeightageInput is a custom component, so it is wrapped in this class and
     styled from outside rather than relying on it accepting a "status" prop. */
  .weightage-error .ant-input,
  .weightage-error .ant-input-affix-wrapper,
  .weightage-error .ant-input-number,
  .weightage-error .ant-input-number-affix-wrapper {
    border-color: ${COLORS.ERROR} !important;
  }
`;

const PERSPECTIVES = [
  "Financial Perspective",
  "Customer Perspective",
  "Internal Business processes",
  "Learning & Growth Perspective",
];

const PERIOD_OPTIONS = [
  { label: "Yearly Appraisal", value: "Yearly" },
  { label: "Half Yearly Appraisal", value: "Half Yearly" },
  { label: "Quarterly Appraisal", value: "Quarterly" },
];

const QUARTER_OPTIONS = [
  { label: "Q1", value: "Q1" },
  { label: "Q2", value: "Q2" },
  { label: "Q3", value: "Q3" },
  { label: "Q4", value: "Q4" },
];

// A Quarterly appraisal only ever covers Q1; Half Yearly only ever covers
// Q1 & Q2 - offering the full Q1-Q4 list regardless of period let users pick
// a quarter their appraisal period couldn't possibly reach.
const quarterOptionsForPeriod = (currentPeriod) => {
  if (currentPeriod === "Quarterly") return QUARTER_OPTIONS.slice(0, 1);
  if (currentPeriod === "Half Yearly") return QUARTER_OPTIONS.slice(0, 2);
  return QUARTER_OPTIONS;
};

const MAX_ROWS = 5;
const MAX_TRAINING_ROWS = 10;

const FIXED_SOFT_SKILLS = [
  { objectives: "Integrity, Honesty (Trusted)", weightage: 18 },
  { objectives: "Effective communication, Inspiration (Hunter)", weightage: 18 },
  { objectives: "Responsibility, buck stops here (Leader)", weightage: 18 },
  { objectives: "Reliability, Resilience (Gatherer)", weightage: 18 },
  { objectives: "Teamwork, Mentorship (Builder)", weightage: 18 },
  { objectives: "Wow Factor (Your Differentiator)", weightage: 10 },
];

const emptyRow = (perspective, sno) => ({
  perspective,
  sno,
  measurementdimension: "",
  objectives: "",
  weightage: 0,
  comments: "",
});

const initialRows = () => {
  const grouped = {};
  PERSPECTIVES.forEach((p) => (grouped[p] = [emptyRow(p, 1)]));
  return grouped;
};

// Builds the 4-perspective Planning grouping from a contract's flat
// `perspectives` array - shared by the initial edit-mode load and by
// "Copy from Previous KPI Planning", capping each perspective at MAX_ROWS.
const buildGroupedFromPerspectives = (perspectives = []) => {
  const grouped = {};
  PERSPECTIVES.forEach((p) => (grouped[p] = []));
  perspectives.forEach((row) => {
    if (!PERSPECTIVES.includes(row.perspective)) return;
    if (grouped[row.perspective].length >= MAX_ROWS) return;
    grouped[row.perspective].push({
      perspective: row.perspective,
      sno: grouped[row.perspective].length + 1,
      measurementdimension: row.measurementdimension || "",
      objectives: row.objectives || "",
      weightage: row.weightage || 0,
      comments: row.comments || "",
    });
  });
  PERSPECTIVES.forEach((p) => {
    if (grouped[p].length === 0) grouped[p] = [emptyRow(p, 1)];
  });
  return grouped;
};

const formatDate = (value) => (value ? dayjs(value).format("Do MMM YYYY") : "-");

const emptyRolloverRow = (sno) => ({ sno, objectives: "", weightage: 0, comments: "" });

const emptyTrainingRow = (sno) => ({ sno, skill: "", method: "", quarter: null });

const REVIEW_LABEL_TO_KEY = { Q1: "q1", "Mid Year": "midyear", Q3: "q3" };

const CreateKpiContract = () => {
  const history = useHistory();
  const location = useLocation();
  // Opened from My Team or the HR tab - Back / Done returns there.
  const backTab = location.state?.fromTab || "myteam";
  const userInfo = useSelector(getUserInfo);
  const record = location.state?.record || {};
  const contractid = location.state?.contractid;
  const isEdit = Boolean(contractid);
  const [form] = Form.useForm();

  const [period, setPeriod] = useState("");

  const [reviews, setReviews] = useState({
    q1: { checked: false, date: null },
    midyear: { checked: false, date: null },
    q3: { checked: false, date: null },
  });
  const [rowsByPerspective, setRowsByPerspective] = useState(initialRows());
  const [rolloverRows, setRolloverRows] = useState([emptyRolloverRow(1)]);
  const [trainingRows, setTrainingRows] = useState([emptyTrainingRow(1)]);
  const [softSkillsEditable, setSoftSkillsEditable] = useState(false);
  const [softSkillsRows, setSoftSkillsRows] = useState(FIXED_SOFT_SKILLS);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [kpiViewOpen, setKpiViewOpen] = useState(false);
  const [appraiseeUserId, setAppraiseeUserId] = useState(
    record.appraiseeuserid || record.userid || null
  );
  const [contractStatus, setContractStatus] = useState(null);
  const [declineReason, setDeclineReason] = useState("");
  const [previousAppraisals, setPreviousAppraisals] = useState([]);
  const [previousModalOpen, setPreviousModalOpen] = useState(false);
  const [selectedPreviousId, setSelectedPreviousId] = useState(null);
  const [previousLoading, setPreviousLoading] = useState(false);
  const [copyLoading, setCopyLoading] = useState(false);
  const [copyTargetPerspective, setCopyTargetPerspective] = useState(null);
  // Flipped on the first submit attempt. Until then no field is flagged, so
  // a fresh form isn't covered in red; after it, borders update live as the
  // user fixes each field.
  const [showErrors, setShowErrors] = useState(false);
  const userList = useSelector(getUserList);

  useEffect(() => {
    // The sticky footer is clamped to the padding edge of this scroll
    // container, so the layout's own bottom padding always shows as a gap
    // below it - drop it to 0 while this page is mounted and restore after.
    const main = document.querySelector(".ant-layout-content");
    if (!main) return;
    const previousPaddingBottom = main.style.paddingBottom;
    main.style.paddingBottom = "0";
    return () => {
      main.style.paddingBottom = previousPaddingBottom;
    };
  }, []);

  useEffect(() => {
    if (!isEdit) return;

    const fetchContract = async () => {
      setLoading(true);
      try {
        const { data, code } = await CALL_API(
          `performance-kpi-management/contract/${contractid}`,
          "get"
        );
        if (code === STATUS_CODE.SUCCESS && data) {
          form.setFieldsValue({
            fullname: data.fullname,
            period: data.period,
            startdate: data.startdate ? dayjs(data.startdate) : null,
          });
          setPeriod(data.period || "Yearly");

          const nextReviews = {
            q1: { checked: false, date: null },
            midyear: { checked: false, date: null },
            q3: { checked: false, date: null },
          };
          (data.progressreviewdates || []).forEach((item) => {
            const key = REVIEW_LABEL_TO_KEY[item.label];
            if (key) {
              nextReviews[key] = { checked: true, date: item.date ? dayjs(item.date) : null };
            }
          });
          setReviews(nextReviews);

          setRowsByPerspective(buildGroupedFromPerspectives(data.perspectives));
          setAppraiseeUserId(data.appraiseeuserid || null);
          setContractStatus(data.planningstatus || null);
          setDeclineReason(data.declinereason || "");
          if (data.rolloverresponsibilities?.length) {
            setRolloverRows(data.rolloverresponsibilities);
          }
          if (data.softskillsassessment?.length) {
            setSoftSkillsRows(data.softskillsassessment);
          }
          if (data.trainingdevelopmentplan?.length) {
            setTrainingRows(data.trainingdevelopmentplan);
          }
          form.setFieldsValue({ appraiseecomment: data.appraiseecomment });
        }
      } catch (error) {
        console.error("Error fetching contract:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchContract();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractid]);

  const onValuesChange = (changed) => {
    if (changed.period) {
      setPeriod(changed.period);
      // Q1 stays valid for every period - only clear a checkpoint that
      // actually contradicts the newly-chosen period, not the whole set.
      if (changed.period === "Quarterly" || changed.period === "Half Yearly") {
        setReviews((prev) => ({
          ...prev,
          midyear: { checked: false, date: null },
          q3: { checked: false, date: null },
        }));
      }
    }
  };

  const toggleReview = (key, checked) => {
    setReviews((prev) => ({ ...prev, [key]: { ...prev[key], checked } }));
  };

  const setReviewDate = (key, date) => {
    setReviews((prev) => ({ ...prev, [key]: { ...prev[key], date } }));
  };

  const updateRow = (perspective, index, field, value) => {
    setRowsByPerspective((prev) => {
      const rows = [...prev[perspective]];
      rows[index] = { ...rows[index], [field]: value };
      return { ...prev, [perspective]: rows };
    });
  };

  const addRow = (perspective) => {
    setRowsByPerspective((prev) => {
      const rows = prev[perspective];
      if (rows.length >= MAX_ROWS) return prev;
      return {
        ...prev,
        [perspective]: [...rows, emptyRow(perspective, rows.length + 1)],
      };
    });
  };

  const weightageTotal = (perspective) =>
    rowsByPerspective[perspective].reduce((sum, row) => sum + (Number(row.weightage) || 0), 0);

  const mainWeightageTotal = PERSPECTIVES.reduce(
    (sum, perspective) => sum + weightageTotal(perspective),
    0
  );

  const updateRolloverRow = (index, field, value) => {
    setRolloverRows((prev) => {
      const rows = [...prev];
      rows[index] = { ...rows[index], [field]: value };
      return rows;
    });
  };

  const addRolloverRow = () => {
    setRolloverRows((prev) =>
      prev.length >= MAX_ROWS ? prev : [...prev, emptyRolloverRow(prev.length + 1)]
    );
  };

  const removeRolloverRow = (index) => {
    setRolloverRows((prev) => {
      const rows = prev.filter((_, i) => i !== index).map((row, i) => ({ ...row, sno: i + 1 }));
      return rows.length ? rows : [emptyRolloverRow(1)];
    });
  };

  const updateTrainingRow = (index, field, value) => {
    setTrainingRows((prev) => {
      const rows = [...prev];
      rows[index] = { ...rows[index], [field]: value };
      return rows;
    });
  };

  const addTrainingRow = () => {
    setTrainingRows((prev) =>
      prev.length >= MAX_TRAINING_ROWS ? prev : [...prev, emptyTrainingRow(prev.length + 1)]
    );
  };

  const removeTrainingRow = (index) => {
    setTrainingRows((prev) => {
      const rows = prev.filter((_, i) => i !== index).map((row, i) => ({ ...row, sno: i + 1 }));
      return rows.length ? rows : [emptyTrainingRow(1)];
    });
  };

  const updateSoftSkillWeightage = (index, value) => {
    setSoftSkillsRows((prev) => {
      const rows = [...prev];
      rows[index] = { ...rows[index], weightage: value };
      return rows;
    });
  };

  const softSkillsTotal = softSkillsRows.reduce((sum, row) => sum + (Number(row.weightage) || 0), 0);

  // Rollover is optional, but a row counts as "started" as soon as any of its
  // fields has content - from then on every field in that row is mandatory.
  const isRolloverStarted = (row) =>
    Boolean(row.objectives || row.comments || Number(row.weightage) > 0);
  const rolloverStartedRows = rolloverRows.filter(isRolloverStarted);
  const rolloverTotal = rolloverRows.reduce((sum, row) => sum + (Number(row.weightage) || 0), 0);

  // Returns a flat { key: true } map of every field that is currently invalid.
  // Key formats:
  //   plan|<perspective>|<sno>|<field>   planning TextAreas
  //   planWeightage                      planning weightage inputs (total != 100)
  //   rollover|<sno>|<field>             rollover row fields (once the row is started)
  //   rolloverWeightage                  rollover weightage inputs (total != 100)
  //   softTotal                          soft skills weightage inputs
  //   training|<sno>|method|quarter      training row fields
  //   review|<q1|midyear|q3>             progress review date pickers
  const computeErrors = () => {
    const errs = {};

    // Each perspective needs at least one fully filled row
    PERSPECTIVES.forEach((perspective) => {
      const rows = rowsByPerspective[perspective];
      const hasCompleteRow = rows.some(
        (r) => r.measurementdimension && r.objectives && r.comments
      );
      if (hasCompleteRow) return;
      rows.forEach((row, index) => {
        const hasAny = row.measurementdimension || row.objectives || row.comments;
        if (index > 0 && !hasAny) return; // ignore untouched extra rows
        ["measurementdimension", "objectives", "comments"].forEach((field) => {
          if (!row[field]) errs[`plan|${perspective}|${row.sno}|${field}`] = true;
        });
      });
    });

    if (mainWeightageTotal !== 100) errs.planWeightage = true;
    // Rollover: optional as a whole, but once a row is started all of its
    // fields are required and the started rows must total 100%.
    rolloverStartedRows.forEach((row) => {
      if (!row.objectives) errs[`rollover|${row.sno}|objectives`] = true;
      if (!(Number(row.weightage) > 0)) errs[`rollover|${row.sno}|weightage`] = true;
      if (!row.comments) errs[`rollover|${row.sno}|comments`] = true;
    });
    if (rolloverStartedRows.length > 0 && rolloverTotal !== 100) errs.rolloverWeightage = true;
    if (softSkillsTotal !== 100) errs.softTotal = true;

    // Same "started" rule as Rollover - any field filled in makes the whole
    // row mandatory, not just once "skill" specifically has content.
    trainingRows.forEach((row) => {
      if (!(row.skill || row.method || row.quarter)) return;
      if (!row.skill) errs[`training|${row.sno}|skill`] = true;
      if (!row.method) errs[`training|${row.sno}|method`] = true;
      if (!row.quarter) errs[`training|${row.sno}|quarter`] = true;
    });

    Object.keys(reviews).forEach((key) => {
      if (reviews[key].checked && !reviews[key].date) errs[`review|${key}`] = true;
    });

    return errs;
  };

  // Live-updating once the user has tried to submit
  const fieldErrors = showErrors ? computeErrors() : {};
  const errStatus = (key) => (fieldErrors[key] ? "error" : undefined);
  const planKey = (row, field) => `plan|${row.perspective}|${row.sno}|${field}`;

  const scrollToFirstError = () => {
    const el = document.querySelector(
      ".ant-form-item-has-error, .ant-input-status-error, .ant-select-status-error, .ant-picker-status-error, .weightage-error"
    );
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const removeRow = (perspective, index) => {
    setRowsByPerspective((prev) => {
      const rows = prev[perspective]
        .filter((_, i) => i !== index)
        .map((row, i) => ({ ...row, sno: i + 1 }));
      return { ...prev, [perspective]: rows.length ? rows : [emptyRow(perspective, 1)] };
    });
  };

  const nameFor = (userid) => userList.find((u) => u.userid === userid)?.fullname || "-";

  const onClickCopyPrevious = async (perspective = null) => {
    if (!appraiseeUserId) {
      message.info("No previous KPI plan found for this employee");
      return;
    }
    setCopyTargetPerspective(perspective);
    setPreviousLoading(true);
    try {
      const { data, code } = await CALL_API(
        `performance-kpi-management/previousappraisals/${appraiseeUserId}`,
        "get"
      );
      const list = code === STATUS_CODE.SUCCESS ? data || [] : [];
      if (!list.length) {
        message.info("No previous KPI plan found for this employee");
        return;
      }
      setPreviousAppraisals(list);
      setSelectedPreviousId(list[0].id);
      setPreviousModalOpen(true);
    } catch (error) {
      console.error("Error fetching previous appraisals:", error);
      message.error("Failed to fetch previous KPI planning");
    } finally {
      setPreviousLoading(false);
    }
  };

  const onCopyObjectives = async () => {
    const target = previousAppraisals.find((item) => item.id === selectedPreviousId);
    if (!target) return;
    setCopyLoading(true);
    try {
      const { data, code } = await CALL_API(
        `performance-kpi-management/contract/${target.contractid}`,
        "get"
      );
      if (code === STATUS_CODE.SUCCESS && data) {
        const grouped = buildGroupedFromPerspectives(data.perspectives);
        if (copyTargetPerspective) {
          setRowsByPerspective((prev) => ({
            ...prev,
            [copyTargetPerspective]: grouped[copyTargetPerspective],
          }));
          message.success(`${copyTargetPerspective} objectives copied from previous KPI Planning`);
        } else {
          setRowsByPerspective(grouped);
          message.success("Objectives copied from previous KPI Planning");
        }
        setPreviousModalOpen(false);
      } else {
        message.error("Failed to copy objectives");
      }
    } catch (error) {
      console.error("Error copying previous objectives:", error);
      message.error("Failed to copy objectives");
    } finally {
      setCopyLoading(false);
    }
  };

  const previousAppraisalColumns = [
    { title: "S.No", key: "sno", width: 60, render: (_, __, index) => index + 1 },
    { title: "Period", dataIndex: "period", key: "period" },
    { title: "Created on", key: "createdon", render: (_, row) => formatDate(row.createdon) },
    { title: "Attended on", key: "attendedon", render: (_, row) => formatDate(row.attendedon) },
    {
      title: "Closed by",
      key: "closedby",
      render: (_, row) => `${nameFor(row.appraiseruserid)}; ${formatDate(row.closedon)}`,
    },
  ];

  const textAreaProps = { autoSize: { minRows: 1, maxRows: 4 } };

  // The 4 perspectives render as ONE continuous table (one shared sticky
  // header) - perspective titles and "Add another" links are injected as
  // full-width rows within the same table via colSpan, rather than each
  // perspective having its own separate Table/header.
  const buildPlanningRows = () => {
    const rows = [];
    PERSPECTIVES.forEach((perspective, i) => {
      if (i > 0) rows.push({ key: `spacer-${perspective}`, isSpacer: true });
      rows.push({ key: `group-${perspective}`, isGroupHeader: true, perspective });
      rowsByPerspective[perspective].forEach((row, index) => {
        rows.push({ ...row, key: `${perspective}-${row.sno}`, localIndex: index });
      });
      if (rowsByPerspective[perspective].length < MAX_ROWS) {
        rows.push({ key: `add-${perspective}`, isAddRow: true, perspective });
      }
    });
    return rows;
  };

  const fullSpanCell = (record) =>
    record.isGroupHeader || record.isAddRow || record.isSpacer ? { colSpan: 6 } : {};
  const hiddenCell = (record) =>
    record.isGroupHeader || record.isAddRow || record.isSpacer ? { colSpan: 0 } : {};

  const planningColumns = [
    {
      title: "S.No",
      key: "sno",
      width: 60,
      onCell: fullSpanCell,
      render: (_, record) => {
        if (record.isSpacer) {
          return null;
        }
        if (record.isGroupHeader) {
          return <div className="perspective-title m-0">{record.perspective}</div>;
        }
        if (record.isAddRow) {
          const isLastPerspective = record.perspective === PERSPECTIVES[PERSPECTIVES.length - 1];
          return (
            <div
              className={`d-flex align-items-center ${
                isLastPerspective ? "justify-content-between" : "justify-content-end"
              }`}
              style={{ gap: 12 }}
            >
              {isLastPerspective && (
                <span style={{ color: mainWeightageTotal === 100 ? COLORS.SUCCESS : COLORS.ERROR }}>
                  Weightage: {mainWeightageTotal}%
                </span>
              )}
              <div className="d-flex align-items-center" style={{ gap: 12 }}>
                <CopyOutlined
                  title="Copy from Previous KPI Planning"
                  style={{ cursor: "pointer", color: COLORS.PRIMARY }}
                  onClick={() => onClickCopyPrevious(record.perspective)}
                />
                <Divider type="vertical" style={{ margin: 0 }} />
                <TextLink onClick={() => addRow(record.perspective)}>
                  <PlusOutlined /> Add another (Max {MAX_ROWS} rows)
                </TextLink>
              </div>
            </div>
          );
        }
        return record.sno;
      },
    },
    {
      title: "Measurement Dimensions",
      key: "measurementdimension",
      onCell: hiddenCell,
      render: (_, record) => (
        <TextArea
          {...textAreaProps}
          status={errStatus(planKey(record, "measurementdimension"))}
          value={record.measurementdimension}
          onChange={(e) =>
            updateRow(record.perspective, record.localIndex, "measurementdimension", e.target.value)
          }
        />
      ),
    },
    {
      title: "Objectives",
      key: "objectives",
      onCell: hiddenCell,
      render: (_, record) => (
        <TextArea
          {...textAreaProps}
          status={errStatus(planKey(record, "objectives"))}
          value={record.objectives}
          onChange={(e) => updateRow(record.perspective, record.localIndex, "objectives", e.target.value)}
        />
      ),
    },
    {
      title: "Weightage (%)",
      key: "weightage",
      width: 130,
      onCell: hiddenCell,
      render: (_, record) => (
        <div className={fieldErrors.planWeightage ? "weightage-error" : ""}>
          <WeightageInput
            value={record.weightage}
            onChange={(val) => updateRow(record.perspective, record.localIndex, "weightage", val)}
          />
        </div>
      ),
    },
    {
      title: "Appraiser's Comment *",
      key: "comments",
      onCell: hiddenCell,
      render: (_, record) => (
        <TextArea
          {...textAreaProps}
          status={errStatus(planKey(record, "comments"))}
          value={record.comments}
          onChange={(e) => updateRow(record.perspective, record.localIndex, "comments", e.target.value)}
        />
      ),
    },
    {
      key: "actions",
      width: 50,
      onCell: hiddenCell,
      render: (_, record) => (
        <DeleteOutlined
          style={{ cursor: "pointer" }}
          onClick={() => removeRow(record.perspective, record.localIndex)}
        />
      ),
    },
  ];

  const rolloverColumns = [
    { title: "S.No", dataIndex: "sno", key: "sno", width: 60 },
    {
      title: "Objectives",
      key: "objectives",
      render: (_, row, index) => (
        <TextArea
          {...textAreaProps}
          status={errStatus(`rollover|${row.sno}|objectives`)}
          value={row.objectives}
          onChange={(e) => updateRolloverRow(index, "objectives", e.target.value)}
        />
      ),
    },
    {
      title: "Weightage (%)",
      key: "weightage",
      width: 130,
      render: (_, row, index) => (
        <div
          className={
            fieldErrors.rolloverWeightage || fieldErrors[`rollover|${row.sno}|weightage`]
              ? "weightage-error"
              : ""
          }
        >
          <WeightageInput
            value={row.weightage}
            onChange={(val) => updateRolloverRow(index, "weightage", val)}
          />
        </div>
      ),
    },
    {
      title: "Appraiser's Comment",
      key: "comments",
      render: (_, row, index) => (
        <TextArea
          {...textAreaProps}
          status={errStatus(`rollover|${row.sno}|comments`)}
          value={row.comments}
          onChange={(e) => updateRolloverRow(index, "comments", e.target.value)}
        />
      ),
    },
    {
      key: "actions",
      width: 50,
      render: (_, row, index) => (
        <DeleteOutlined style={{ cursor: "pointer" }} onClick={() => removeRolloverRow(index)} />
      ),
    },
  ];

  const softSkillsColumns = [
    { title: "Objectives", dataIndex: "objectives", key: "objectives" },
    {
      title: "Weightage (%)",
      key: "weightage",
      width: 130,
      render: (_, row, index) =>
        softSkillsEditable ? (
          <div className={fieldErrors.softTotal ? "weightage-error" : ""}>
            <WeightageInput
              value={row.weightage}
              onChange={(val) => updateSoftSkillWeightage(index, val)}
            />
          </div>
        ) : (
          row.weightage
        ),
    },
  ];

  const trainingColumns = [
    { title: "S.No", dataIndex: "sno", key: "sno", width: 60 },
    {
      title: "Skill to be developed / strengthened",
      key: "skill",
      render: (_, row, index) => (
        <TextArea
          {...textAreaProps}
          status={errStatus(`training|${row.sno}|skill`)}
          value={row.skill}
          onChange={(e) => updateTrainingRow(index, "skill", e.target.value)}
        />
      ),
    },
    {
      title: "Method of development, i.e. whether on-the-job , formal training or other.",
      key: "method",
      render: (_, row, index) => (
        <TextArea
          {...textAreaProps}
          status={errStatus(`training|${row.sno}|method`)}
          value={row.method}
          onChange={(e) => updateTrainingRow(index, "method", e.target.value)}
        />
      ),
    },
    {
      title: "Planned / Expected quarter",
      key: "quarter",
      width: 160,
      render: (_, row, index) => (
        <Select
          status={errStatus(`training|${row.sno}|quarter`)}
          options={quarterOptionsForPeriod(period)}
          value={row.quarter}
          onChange={(val) => updateTrainingRow(index, "quarter", val)}
          style={{ width: "100%" }}
          placeholder="Select"
        />
      ),
    },
    {
      key: "actions",
      width: 50,
      render: (_, row, index) => (
        <DeleteOutlined style={{ cursor: "pointer" }} onClick={() => removeTrainingRow(index)} />
      ),
    },
  ];

  const onCreate = async () => {
    let values;
    let formInvalid = false;
    try {
      values = await form.validateFields();
    } catch (err) {
      formInvalid = true; // antd renders the inline field errors for the top form
    }

    const errs = computeErrors();
    if (formInvalid || Object.keys(errs).length) {
      setShowErrors(true);
      // Soft-skills weightage inputs are hidden unless editable, so reveal them
      if (errs.softTotal) setSoftSkillsEditable(true);
      setTimeout(scrollToFirstError, 100); // wait for the red classes to render
      return;
    }

    const progressreviewdates = [];
    if (reviews.q1.checked) progressreviewdates.push({ label: "Q1", date: reviews.q1.date });
    if (reviews.midyear.checked)
      progressreviewdates.push({ label: "Mid Year", date: reviews.midyear.date });
    if (reviews.q3.checked) progressreviewdates.push({ label: "Q3", date: reviews.q3.date });

    const perspectives = Object.values(rowsByPerspective)
      .flat()
      .filter((row) => row.measurementdimension || row.objectives);

    const rolloverresponsibilities = rolloverRows.filter(isRolloverStarted);
    const trainingdevelopmentplan = trainingRows.filter((row) => row.skill);

    setSubmitLoading(true);
    try {
      const isResend = ["declined", "revisionrequested"].includes(contractStatus);
      const { code } = isEdit
        ? await CALL_API(`performance-kpi-management/editcontract/${contractid}`, "patch", {
            fullname: values.fullname,
            period: values.period,
            startdate: values.startdate,
            progressreviewdates,
            perspectives,
            rolloverresponsibilities,
            softskillsassessment: softSkillsRows,
            trainingdevelopmentplan,
            appraiseecomment: values.appraiseecomment || "",
            ...(isResend && { planningstatus: "yettoaccept", declinereason: "" }),
          })
        : await CALL_API("performance-kpi-management/contract", "post", {
            appraiseeuserid: record.appraiseeuserid || record.userid,
            // HR creates on the manager's behalf - the employee's own
            // reporting manager stays the appraiser.
            appraiseruserid: record.appraiseruserid || userInfo?.userid,
            fullname: values.fullname,
            employeeid: record.employeeid,
            period: values.period,
            startdate: values.startdate,
            kpidefinedby: userInfo?.name,
            progressreviewdates,
            perspectives,
            rolloverresponsibilities,
            softskillsassessment: softSkillsRows,
            trainingdevelopmentplan,
            appraiseecomment: values.appraiseecomment || "",
          });
      if (code === STATUS_CODE.SUCCESS) {
        message.success(
          isEdit
            ? isResend
              ? "KPI resent successfully"
              : "KPI updated successfully"
            : "KPI created successfully"
        );
        history.push("/performance-kpi", { id: "kpiplanning", subTab: backTab });
      } else {
        message.error(isEdit ? "Failed to update KPI" : "Failed to create KPI");
      }
    } catch (error) {
      console.error("Error saving contract:", error);
      message.error(isEdit ? "Failed to update contract" : "Failed to create contract");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Wrap>
      <div className="breadcrumb mb-3">
        <p
          className="lite-text p-0 m-0"
          style={{ cursor: "pointer" }}
          onClick={() => history.push("/performance-kpi", { id: "kpiplanning", subTab: backTab })}
        >
          Performance Management
        </p>
        &nbsp;
        <RightOutlined className="mt-1" />
        &nbsp;
        <p className="m-0 p-0">{isEdit ? "Editing KPI" : "Creating KPI"}</p>
      </div>

      <h6>{isEdit ? "Editing KPI" : "Performance KPI"}</h6>

      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{ fullname: record.fullname, period: "" }}
          onValuesChange={onValuesChange}
        >
          <Row gutter={[16, 16]} className="my-3">
            <Col xs={24} md={12}>
              <Form.Item
                name="fullname"
                label="Name of the employee"
                rules={[{ required: true, message: "Name of the employee is required" }]}
              >
                <Input disabled />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="period"
                label="Period"
                rules={[{ required: true, message: "Period is required" }]}
              >
                <Select options={PERIOD_OPTIONS} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="startdate"
                label="Start date to attend appraisal"
                rules={[{ required: true, message: "Start date to attend appraisal is required" }]}
              >
                <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
              </Form.Item>
            </Col>
            {period !== "Quarterly" && (
              <Col xs={24} md={12}>
                <div className="lite-text mb-2">
                  If you need progress review, select the sessions you want and set a start date for each
                </div>
                <div className="review-checkbox-row">
                  <Checkbox
                    className="review-checkbox-label"
                    checked={reviews.q1.checked}
                    onChange={(e) => toggleReview("q1", e.target.checked)}
                  >
                    Q1 Review
                  </Checkbox>
                  <DatePicker
                    status={errStatus("review|q1")}
                    disabled={!reviews.q1.checked}
                    value={reviews.q1.date}
                    onChange={(d) => setReviewDate("q1", d)}
                    format="DD-MM-YYYY"
                  />
                </div>
                {period === "Yearly" && (
                  <>
                    <div className="review-checkbox-row">
                      <Checkbox
                        className="review-checkbox-label"
                        checked={reviews.midyear.checked}
                        onChange={(e) => toggleReview("midyear", e.target.checked)}
                      >
                        Mid-Year Review
                      </Checkbox>
                      <DatePicker
                        status={errStatus("review|midyear")}
                        disabled={!reviews.midyear.checked}
                        value={reviews.midyear.date}
                        onChange={(d) => setReviewDate("midyear", d)}
                        format="DD-MM-YYYY"
                      />
                    </div>
                    <div className="review-checkbox-row">
                      <Checkbox
                        className="review-checkbox-label"
                        checked={reviews.q3.checked}
                        onChange={(e) => toggleReview("q3", e.target.checked)}
                      >
                        Q3 Review
                      </Checkbox>
                      <DatePicker
                        status={errStatus("review|q3")}
                        disabled={!reviews.q3.checked}
                        value={reviews.q3.date}
                        onChange={(d) => setReviewDate("q3", d)}
                        format="DD-MM-YYYY"
                      />
                    </div>
                  </>
                )}
              </Col>
            )}
          </Row>

          {declineReason && ["declined", "revisionrequested"].includes(contractStatus) && (
            <div className="decline-reason-box">
              <div className="decline-reason-label">
                {contractStatus === "revisionrequested"
                  ? "Reason for Revision Request"
                  : "Reason for Decline"}
              </div>
              <div>{declineReason}</div>
            </div>
          )}

          <div className="d-flex justify-content-between align-items-center" style={{ marginTop: 56 }}>
            <div>
              <h6 className="m-0">Planning *</h6>
              <p className="lite-text m-0">
                Describe specific Objectives or other developmental activities to be
                undertaken by Appraisee during the appraisal period.
              </p>
            </div>
            <TextLink onClick={() => onClickCopyPrevious()} loading={previousLoading}>
              <CopyOutlined /> Copy from Previous KPI Planning
            </TextLink>
          </div>

          <Table
            columns={planningColumns}
            dataSource={buildPlanningRows()}
            pagination={false}
            rowKey={(row) => row.key}
            showHeader
            sticky={{ getContainer: () => document.querySelector(".ant-layout-content") }}
            rowClassName={(record) =>
              record.isGroupHeader ? "planning-group-row" : record.isSpacer ? "planning-spacer-row" : ""
            }
          />

          {rowsByPerspective[PERSPECTIVES[PERSPECTIVES.length - 1]].length >= MAX_ROWS && (
            // Weightage normally rides along on the last perspective's "Add
            // another" row (same row, not a new one) - only needed as its own
            // line here once that perspective is full and has no add-row left
            // to attach to.
            <div className="mb-2">
              <span style={{ color: mainWeightageTotal === 100 ? COLORS.SUCCESS : COLORS.ERROR }}>
                Weightage: {mainWeightageTotal}%
              </span>
            </div>
          )}

          <div className="perspective-title" style={{ marginTop: 56 }}>
            Rollover Responsibility from Previous Appraisal
          </div>
          <p className="lite-text m-0 mb-2">
            Optional. If you start filling a row, Objectives, Weightage and Appraiser's Comment are
            all required.
          </p>
          <Table
            columns={rolloverColumns}
            dataSource={rolloverRows}
            pagination={false}
            rowKey={(row) => `rollover-${row.sno}`}
            footer={() => (
              <div className="d-flex justify-content-between align-items-center">
                {rolloverStartedRows.length > 0 ? (
                  <span style={{ color: rolloverTotal === 100 ? COLORS.SUCCESS : COLORS.ERROR }}>
                    Weightage: {rolloverTotal}%
                  </span>
                ) : (
                  <span />
                )}
                {rolloverRows.length < MAX_ROWS ? (
                  <TextLink onClick={addRolloverRow}>
                    <PlusOutlined /> Add another (Max {MAX_ROWS} rows)
                  </TextLink>
                ) : (
                  <span />
                )}
              </div>
            )}
          />

          <div className="d-flex justify-content-between align-items-center" style={{ marginTop: 56 }}>
            <div className="perspective-title m-0">Individual (soft skills) Assessment</div>
            {softSkillsEditable ? (
              <TextLink onClick={() => setSoftSkillsRows(FIXED_SOFT_SKILLS)}>
                Use Default Weightage
              </TextLink>
            ) : (
              <TextLink onClick={() => setSoftSkillsEditable(true)}>Edit Weightage</TextLink>
            )}
          </div>
          <Table
            columns={softSkillsColumns}
            dataSource={softSkillsRows}
            pagination={false}
            rowKey={(row) => row.objectives}
            footer={
              softSkillsEditable
                ? () => (
                    <div className="text-end">
                      <span style={{ color: softSkillsTotal === 100 ? COLORS.SUCCESS : COLORS.ERROR }}>
                        Total Weightage: {softSkillsTotal}%
                      </span>
                    </div>
                  )
                : undefined
            }
          />

          <div className="perspective-title" style={{ marginTop: 56 }}>
            Training & Development Plan
          </div>
          <p className="lite-text m-0">
            Based on the strengths and limitations identified in the previous review, indicate the
            work experience and/or training suggested during the plan period.
          </p>
          <Table
            columns={trainingColumns}
            dataSource={trainingRows}
            pagination={false}
            rowKey={(row) => `training-${row.sno}`}
            footer={() =>
              trainingRows.length < MAX_TRAINING_ROWS ? (
                <TextLink onClick={addTrainingRow}>
                  <PlusOutlined /> Add another (Max {MAX_TRAINING_ROWS} rows)
                </TextLink>
              ) : null
            }
          />

          <Form.Item name="appraiseecomment" label="Appraiser's Comment" className="mt-4">
            <TextArea autoSize={{ minRows: 1, maxRows: 4 }} />
          </Form.Item>
        </Form>

        <UserFloatingButton setIsViewKpi={setKpiViewOpen} bottompossition="80px" />

        <div className="footer">
          <ButtonOutlined
            onClick={() => history.push("/performance-kpi", { id: "kpiplanning", subTab: backTab })}
          >
            Cancel
          </ButtonOutlined>
          <ButtonFilled type="primary" loading={submitLoading} onClick={onCreate}>
            {isEdit ? (contractStatus === "declined" ? "Resend" : "Save Changes") : "Create"}
          </ButtonFilled>
        </div>
      </Spin>

      <KpiViewModal open={kpiViewOpen} onClose={() => setKpiViewOpen(false)} />

      <Modal
        title="Previous Appraisals"
        open={previousModalOpen}
        onCancel={() => setPreviousModalOpen(false)}
        width={800}
        centered
        footer={[
          <ButtonOutlined key="cancel" onClick={() => setPreviousModalOpen(false)}>
            Cancel
          </ButtonOutlined>,
          <ButtonFilled
            key="copy"
            type="primary"
            loading={copyLoading}
            disabled={!selectedPreviousId}
            onClick={onCopyObjectives}
          >
            Copy Objectives
          </ButtonFilled>,
        ]}
      >
        <Table
          columns={previousAppraisalColumns}
          dataSource={previousAppraisals}
          pagination={false}
          rowKey={(row) => row.id}
          rowSelection={{
            type: "radio",
            selectedRowKeys: selectedPreviousId ? [selectedPreviousId] : [],
            onChange: (keys) => setSelectedPreviousId(keys[0]),
          }}
          onRow={(row) => ({
            style: { cursor: "pointer" },
            onClick: () => setSelectedPreviousId(row.id),
          })}
        />
      </Modal>
    </Wrap>
  );
};

export default CreateKpiContract;